import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import {
    GET_REPORT_REASONS,
    GetReportReasonsData,
    GetReportReasonsVariables,
    REPORT_MEDIA,
    ReportMediaData,
    ReportMediaVariables,
    REPORT_CHANNEL,
    ReportChannelData,
    ReportChannelVariables,
    ReportReason,
} from '@jwp/ott-common/src/graphql/query/reportModeration.queries';
import styles from './ReportChannelModal.module.scss';
import { showToast } from '@jwp/ott-common/src/stores/ToastStore';
import CloseChannelIcon from '@jwp/ott-theme/assets/icons/Close_channel.svg?react';

type ReportChannelModalProps = {
    channelId?: string;
    mediaId?: string;
    onClose?: () => void;
    onSuccess?: () => void; // API success aagumbodhu query refetch panna idhu thaan right prop
};


const ReportChannelModal: React.FC<ReportChannelModalProps> = ({
    channelId: propChannelId,
    mediaId,
    onClose,
    onSuccess,
}) => {
    const { chId: paramChId } = useParams<{ chId: string }>();
    const activeChannelId = propChannelId || paramChId || '';

    const { t } = useTranslation(['user', 'common']);
    const [step, setStep] = useState<1 | 2>(1);
    const [selectedReasonCode, setSelectedReasonCode] = useState<string>('');
    const [detailsText, setDetailsText] = useState<string>('');

    const [reportChannel, { loading: submittingChannel }] = useMutation<
        ReportChannelData,
        ReportChannelVariables
    >(REPORT_CHANNEL, {
        client: getAuthApolloClient(),
    });


    const [reportMedia, { loading: submittingMedia }] = useMutation<
        ReportMediaData,
        ReportMediaVariables
    >(REPORT_MEDIA, {
        client: getAuthApolloClient(),
    });

    const { data: reportReasonsData, loading, error } = useQuery<
        GetReportReasonsData,
        GetReportReasonsVariables
    >(GET_REPORT_REASONS, {
        client: getAuthApolloClient(),
        variables: {},
        errorPolicy: 'all',
        fetchPolicy: 'cache-and-network',
    });

    const reportReasons: ReportReason[] = reportReasonsData?.getReportReasons || [];

    const handleReasonSelect = (rrCode: string) => {
        setSelectedReasonCode(rrCode);
        setStep(2);
    };

    const handleBack = () => {
        setStep(1);
    };

    const submitting = submittingChannel || submittingMedia;

    const handleSubmit = async () => {
        if (!selectedReasonCode || (!activeChannelId && !mediaId)) return;

        try {
            if (mediaId) {
                await reportMedia({
                    variables: {
                        input: {
                            maId: mediaId,
                            reasonCode: selectedReasonCode,
                            details: detailsText,
                        },
                    },
                });
            } else {
                await reportChannel({
                    variables: {
                        input: {
                            chId: activeChannelId,
                            reasonCode: selectedReasonCode,
                            details: detailsText,
                        },
                    },
                });
            }

            // API Success aana thaan onSuccess trigger aagum (Refetch kaga)
            if (onSuccess) {
                onSuccess();
                showToast(
                    t('common:report_submitted', {
                        defaultValue: 'Report submitted successfully.',
                    }),
                    'success'
                );
            }

            onClose?.();
        } catch (err: any) {
            console.error('Failed to report channel:', err);
            const graphQLErrorMessage = err?.graphQLErrors?.[0]?.message || err?.message;

            if (graphQLErrorMessage?.includes('already reported')) {
                showToast(
                    t('channel.already_reported_toast.message', {
                        defaultValue: 'Already Reported',
                    }),
                    'error'
                );
            } else {
                showToast(
                    t('common:something_went_wrong', {
                        defaultValue: 'Something went wrong. Please try again.',
                    }),
                    'error'
                );
            }
            onClose?.();
        }
    };

    return (
        <div className={styles.overlay}>
            <div className={classNames(styles.modalContainer, {
                [styles.modalContainerTwo]: step === 2,
            })}>
                {/* Header */}
                <div className={styles.header}>
                    <h2 className={styles.title}>{t('user:reports.report_channel', 'Report Channel')}</h2>
                    {onClose && (
                        <button
                            type="button"
                            className={styles.closeButton}
                            onClick={onClose}
                            aria-label="Close modal"
                        >
                            <CloseChannelIcon />
                        </button>
                    )}
                </div>

                {/* Step 1: Dynamic Report Reasons List */}
                {step === 1 && (
                    <div className={styles.reasonsList}>
                        {loading && <div className={styles.loadingState}>Loading reasons...</div>}

                        {error && !reportReasons.length && (
                            <div className={styles.errorState}>Failed to load reasons</div>
                        )}

                        {!loading &&
                            reportReasons.map((reason) => {
                                const isSelected = selectedReasonCode === reason.rrCode;
                                return (
                                    <label
                                        key={reason.rrId}
                                        className={`${styles.radioOption} ${isSelected ? styles.selectedOption : ''}`}
                                    >
                                        <div className={styles.radioButton}>
                                            <input
                                                type="radio"
                                                name="reportReason"
                                                value={reason.rrCode}
                                                checked={isSelected}
                                                onClick={() => handleReasonSelect(reason.rrCode)}
                                            />
                                            <span className={styles.radioCustom} />
                                        </div>
                                        <span className={styles.radioLabel}>{reason.rrLabel}</span>
                                    </label>
                                );
                            })}
                    </div>
                )}

                {/* Step 2: Confirm Selected Reason & Add Details */}
                {step === 2 && (
                    <div className={styles.stepTwoContainer}>
                        {/* Optional Details Input */}
                        <div className={styles.inputGroup}>
                            <label htmlFor="reportDetails" className={styles.inputLabel}>
                                {t('user:reports.add_details', 'Add details')}
                            </label>
                            <textarea
                                id="reportDetails"
                                className={styles.textarea}
                                placeholder={t(
                                    'user:reports.add_details_placeholder',
                                    'Sharing a few details can help us understand the issue.'
                                )}
                                value={detailsText}
                                onChange={(e) => setDetailsText(e.target.value)}
                                rows={10}
                            />
                        </div>
                    </div>
                )}
                {/* Actions */}
                {step === 2 && <div className={styles.footerActions}>
                    <button
                        type="button"
                        className={styles.backButton}
                        onClick={handleBack}
                        disabled={submitting}
                    >
                        {t('common:back', 'Back')}
                    </button>
                    <button
                        type="button"
                        className={styles.submitButton}
                        onClick={handleSubmit}
                        disabled={submitting || !selectedReasonCode || !detailsText.trim()}
                    >
                        {submitting ? 'Submitting...' : t('common:submit', 'Submit Report')}
                    </button>
                </div>}
            </div>
        </div>
    );
};

export default ReportChannelModal;